/**
 * Analytics Service Layer
 *
 * Provides a clean, type-safe interface for analytics and dashboard operations.
 * Used by React Query hooks for data fetching.
 */

import { apiClient } from './api.client';
import type {
  DashboardAnalyticsResponse,
  ActivityFeedResponse,
  AnalyticsByPeriodResponse,
  AnalyticsPeriod,
} from '@lnr-v2/types';

/**
 * Analytics Service
 * All analytics-related API operations
 */
export const analyticsService = {
  /**
   * Get dashboard KPIs and metrics
   * Returns all dashboard data in a single optimized request
   */
  async getDashboardKPIs(): Promise<DashboardAnalyticsResponse> {
    return apiClient
      .get('analytics/dashboard')
      .json<DashboardAnalyticsResponse>();
  },

  /**
   * Get recent activity feed with pagination
   */
  async getRecentActivity(
    page: number = 1,
    limit: number = 20
  ): Promise<ActivityFeedResponse> {
    const params = new URLSearchParams();
    params.append('page', page.toString());
    params.append('limit', limit.toString());

    return apiClient
      .get(`analytics/activity?${params.toString()}`)
      .json<ActivityFeedResponse>();
  },

  /**
   * Get analytics for specific time period
   */
  async getAnalyticsByPeriod(
    period: AnalyticsPeriod = '30D'
  ): Promise<AnalyticsByPeriodResponse> {
    const params = new URLSearchParams();
    params.append('period', period);

    return apiClient
      .get(`analytics/period?${params.toString()}`)
      .json<AnalyticsByPeriodResponse>();
  },
};
